// Le proposte e i calcoli della procedura «Nuovo progetto»: team e fasi di partenza (tutte
// modificabili), il nome e la variante del pop di un membro nuovo, le date delle fasi.
import { api } from '../../../lib/api';
import type { AgentProfile, PlanMember, PlanPhase, ProjectPlan } from '../../../lib/api';
import { popRole, popVariants } from '../../../lib/pops';

export const EMPTY_PLAN: ProjectPlan = {
  requirements: '',
  start: null,
  pipeline: null,
  team: [],
  phases: [],
};

/** Il pop di un membro nuovo: quello del profilo se è libero, altrimenti un'altra variante dello
 *  stesso ruolo non ancora presa (la regola dei due developer). */
export function freeAvatar(profile: AgentProfile, team: PlanMember[]): string {
  const taken = new Set(team.map((m) => m.avatar));
  if (!taken.has(profile.avatar)) return profile.avatar;
  return popVariants(popRole(profile.avatar)).find((a) => !taken.has(a)) ?? profile.avatar;
}

// i nomi per i doppioni (regola del 2026-10-31): li dà il server, dalla configurazione
let POOL: string[] = [];
export async function loadPopNames(): Promise<void> {
  if (POOL.length) return;
  try {
    POOL = await api.popNames();
  } catch {
    POOL = [];
  }
}

/** Il nome di un membro nuovo: quello del pop se è libero, altrimenti uno a caso fra i nomi
 *  dell'elenco non ancora presi; senza elenco «Sviluppatore 2», «Sviluppatore 3»… */
export function freeName(profile: AgentProfile, team: PlanMember[]): string {
  const names = new Set(team.map((m) => m.name));
  const own = profile.nickname || profile.label;
  if (!names.has(own)) return own;
  const free = POOL.filter((n) => !names.has(n));
  if (free.length) return free[Math.floor(Math.random() * free.length)];
  let n = 2;
  while (names.has(`${own} ${n}`)) n += 1;
  return `${own} ${n}`;
}

export function memberOf(profile: AgentProfile, team: PlanMember[]): PlanMember {
  return {
    name: freeName(profile, team),
    profile: profile.id,
    avatar: freeAvatar(profile, team),
    model: profile.model,
    phase: '',
    instructions: '',
  };
}

const ROLE_ORDER = ['team-lead', 'developer', 'reviewer', 'qa'];

/** Il team di partenza: un pop per ruolo di lavoro fra quelli del catalogo (il coordinatore
 *  scompone i piani e non entra nel team). */
export function proposeTeam(profiles: AgentProfile[]): PlanMember[] {
  const team: PlanMember[] = [];
  for (const role of ROLE_ORDER) {
    const p = profiles.find((x) => !x.coordinator && popRole(x.avatar) === role);
    if (p) team.push(memberOf(p, team));
  }
  return team;
}

/** Le fasi di partenza, con il responsabile preso dal team quando c'è. */
export function proposePhases(team: PlanMember[], profiles: AgentProfile[]): PlanPhase[] {
  const who = (role: string) => {
    const m = team.find((x) => {
      const p = profiles.find((y) => y.id === x.profile);
      return p && popRole(p.avatar) === role;
    });
    return m?.name ?? '';
  };
  return [
    {
      name: 'Analisi',
      description: 'Legge requisiti e codice, scrive il piano tecnico e divide il lavoro.',
      owner: who('team-lead'),
      model: null,
      days: 1,
    },
    {
      name: 'Sviluppo',
      description: 'Scrive il codice dei lavori del piano, ognuno sul suo branch.',
      owner: who('developer'),
      model: null,
      days: 3,
    },
    {
      name: 'Review',
      description: 'Controlla ogni lavoro contro i criteri; quello che non va torna indietro.',
      owner: who('reviewer'),
      model: null,
      days: 1,
    },
    {
      name: 'Consegna',
      description: 'Apre la pull request verso il branch di partenza e scrive il resoconto.',
      owner: who('team-lead'),
      model: null,
      days: 1,
    },
  ];
}

const DAY = 86_400_000;
const iso = (d: Date) => d.toISOString().slice(0, 10);

/** Le date delle fasi, una dopo l'altra, contando solo i giorni lavorativi (come il server). */
export function schedule(plan: ProjectPlan): { from: string; to: string }[] {
  let at = new Date(`${plan.start ?? iso(new Date())}T12:00:00Z`);
  const work = (d: Date) => d.getUTCDay() !== 0 && d.getUTCDay() !== 6;
  return plan.phases.map((p) => {
    while (!work(at)) at = new Date(at.getTime() + DAY);
    const first = at;
    let last = first;
    let left = p.days - 1;
    while (left > 0) {
      last = new Date(last.getTime() + DAY);
      if (work(last)) left -= 1;
    }
    at = new Date(last.getTime() + DAY);
    return { from: iso(first), to: iso(last) };
  });
}

const SHORT = new Intl.DateTimeFormat('it-IT', { weekday: 'short', day: 'numeric' });
export const shortDay = (isoDay: string) => SHORT.format(new Date(`${isoDay}T12:00:00Z`));

/** La cartella che il server ricava dal nome quando non se ne sceglie una (projects.slug). */
export const folderOf = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'progetto';
